/**
 * The words an operator reads for an archetype or an industry.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A MODULE AND NOT A `.find()` AT EACH CALL SITE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `ARCHETYPES` already carried a `label` for every archetype, and the onboarding view
 * carried a private `INDUSTRIES` list with a label for all fifteen. Everywhere ELSE printed
 * the raw identifier — the site hub header read `datacenter · telecom`, the Standards page
 * said "graded as datacenter", the fleet table showed the reporting-state enum beside its
 * own written description. Slugs are database values; they are not English, and a customer
 * reading `oilgas` where they expect "Oil & Gas" is reading a leak of our schema.
 *
 * The labels existed. What did not exist was a way to reach them from outside the one
 * screen that owned them, so every other screen fell back to the id.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY BOTH FALL BACK TO THE ID RATHER THAN THROWING
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * These render values that arrive from the API, and the API is deployed separately. A site
 * created after a backend that knows a sixteenth industry, read by a frontend that does
 * not, must still show something. An unstyled slug is a poor label; a crashed page is a
 * worse one, and the header would be the thing that crashed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE IS `lib/compliance-labels.ts` AND NOT `lib/compliance/labels.ts`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It lived inside `lib/compliance/` for four days, and `npm run compliance:check` failed on
 * every build for those four days. That directory is a byte-identical mirror of the backend's
 * engine package: its digest hashes every `.ts` file under it BY PATH, so a frontend-only
 * module inside it can never match the backend's DIGEST, and the sync script would delete it
 * on the next run. The same commit also edited one word of the synced `templates.ts`
 * ("Center" → "Centre"), which is the one thing the working agreement forbids. Both are
 * undone; the spelling now lives in `ARCHETYPE_LABEL` below, where console copy belongs.
 *
 * The sibling `compliance-budgets.test.ts` is the precedent: frontend files ABOUT the engine
 * sit beside the synced directory, never inside it.
 */
import { ARCHETYPES } from './compliance/templates';
import type { ArchetypeId, IndustryId } from './compliance/types';

/**
 * Industry id to label — the operator's vocabulary.
 *
 * Lives here rather than in the onboarding screen because industry is a DOMAIN term, not a
 * property of the form that first collects it. `INDUSTRY_IDS` is next door in `types.ts`
 * for the same reason, and the two are meant to be read together: an id with no label here
 * is a gap the type checker will not catch, because the map is keyed by the union.
 */
export const INDUSTRY_LABEL: Record<IndustryId, string> = {
  datacenter: 'Data Centre',
  telecom: 'Telecom −48V',
  ev: 'EV Charging',
  solar: 'Solar Hybrid',
  ups: 'UPS / Mission-critical',
  healthcare: 'Healthcare',
  industrial: 'Industrial / Process',
  oilgas: 'Oil & Gas',
  rail: 'Rail & Transit',
  water: 'Water / Wastewater',
  broadcast: 'Broadcast',
  retail: 'Retail & Branch',
  bess: 'Battery Storage',
  wind: 'Wind',
  microgrid: 'Microgrid',
};

/**
 * CONSOLE SPELLING FOR AN ARCHETYPE, where it differs from the engine's own label.
 *
 * `ARCHETYPES[].label` is the engine's word, and the engine is a synced copy that may not be
 * edited here. The customer-facing spelling is a property of THIS console, so it is declared
 * here and the engine label is the fallback for everything not listed. Keep this to spelling:
 * a different NAME for an archetype is a domain decision and belongs in the engine.
 */
const ARCHETYPE_LABEL: Partial<Record<ArchetypeId, string>> = {
  datacenter: 'Data Centre',
};

/** The written name of an archetype, e.g. `datacenter` -> "Data Centre". */
export function archetypeLabel(id: string | null | undefined): string {
  if (id == null || id === '') return '—';
  return (
    ARCHETYPE_LABEL[id as ArchetypeId] ??
    ARCHETYPES.find((a) => a.id === (id as ArchetypeId))?.label ??
    id
  );
}

/** The written name of an industry, e.g. `oilgas` -> "Oil & Gas". */
export function industryLabel(id: string | null | undefined): string {
  if (id == null || id === '') return '—';
  return INDUSTRY_LABEL[id as IndustryId] ?? id;
}

/* ═══════════════════════════════════════════════════════ standards domains ══ */

/**
 * The thirteen standards domains, by id.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS TRANSCRIBED AND NOT IMPORTED, WHEN `ARCHETYPES` ABOVE IS IMPORTED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `DOMAINS` exists in the engine, and reading it from there would be the obvious thing. It
 * cannot be done from code that ships in the ENTRY chunk, and this was measured rather than
 * assumed:
 *
 *   `DOMAINS` is exported from `compliance/standards/index.ts`, and that module imports all
 *   thirty-eight standards to build `STANDARDS` and `ALL` beside it. There is no deeper module
 *   holding the domain metadata alone, so every route to `DOMAINS` — barrel or deep — drags the
 *   whole catalogue in.
 *
 * `FindingsButton` is rendered in the app header on every screen, so it is EAGER. Importing
 * `DOMAINS` there put 109 checks into the entry chunk and took it from 181.8 KB gzipped to
 * 242.8 KB, over a 200 KB budget whose whole point is that every cold load pays it before the
 * sign-in screen can paint. `bundle:budget` failed, correctly.
 *
 * `ARCHETYPES` above is a different case and stays an import: `compliance/templates` is a leaf
 * of parameter templates that pulls in no standards.
 *
 * ── THE DUPLICATION IS GUARDED, NOT TRUSTED ──────────────────────────────────
 * A hand-copied list drifts. `compliance-labels.test.ts` imports `DOMAINS` from the engine —
 * a TEST may, since tests are never bundled — and asserts this record covers every id with
 * the same name. Add a domain to the engine and that test fails before the gap can ship.
 */
export const DOMAIN_LABEL: Record<string, string> = {
  topology: 'Topology & Resilience',
  telecomdc: 'Telecom DC Power',
  fmps: 'Fault-Managed & Remote Power',
  grounding: 'Grounding, Bonding & Surge',
  structural: 'Structural & Environmental',
  cabling: 'Cabling & Pathways',
  intl: 'International & OAM',
  lifesafety: 'Life Safety & Essential Power',
  hazardous: 'Hazardous Areas',
  process: 'Industrial & Process',
  generation: 'Generation & Interconnection',
  storage: 'Energy Storage',
  mobility: 'EV & Traction',
};

/** A domain's written name, falling back to the id for the same reason the two above do. */
export function domainLabel(id: string | null | undefined): string {
  if (!id) return '—';
  return DOMAIN_LABEL[id] ?? id;
}
